import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  useOpenYeshumanContactSheet,
} from '@/components/landing/yeshuman/yeshuman-contact-sheet-provider'
import {
  CloseMark,
  HeadlineUnderline,
  HeroMark,
  NameUnderline,
  OverlapMark,
  PencilFilter,
  PromiseMark,
  SectionRule,
  StepMark,
  useSketchMarks,
} from '@/components/landing/yeshuman/yeshuman-sketch-marks'
import { YeshumanMarketingChrome } from '@/components/landing/yeshuman/yeshuman-marketing-chrome'
import {
  yeshumanDisplayHeading,
  yeshumanEditorial,
  yeshumanEditorialDisplay,
  yeshumanEditorialSm,
  yeshumanHeroHeading,
  yeshumanTabular,
  yeshumanUiSans,
} from '@/components/landing/yeshuman/yeshuman-typography'
import {
  yeshumanBand,
  yeshumanBandAlt,
  yeshumanContentColumn,
} from '@/components/landing/yeshuman/yeshuman-layout'
import { PublicPageHead } from '@/components/public/public-page-head'
import { Button } from '@/components/ui/button'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { resolveMarketingStickyNavChrome } from '@/landing/layouts/landing-layouts'
import { resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'

const PAGE_TITLE = 'Yes Human | Ship the AI product your prototype promised'

const sectionPad = 'py-[clamp(80px,9vw,136px)]'

const HELP = [
  {
    term: 'Your prototype is not ready for customers',
    desc: 'The demo shows the potential. Now it needs to handle volume, real data, real edge cases and the expectations of your stakeholders.',
  },
  {
    term: 'You need help without hiring a whole team',
    desc: 'You get product strategy, AI expertise and hands-on building in the same small team. We help you make the important choices and put the first version to work before you commit to a larger team or investment.',
  },
]

const STEPS = [
  {
    term: 'Working session',
    desc: 'An hour together. We look at what you have, even if it is only an idea on paper, what problem it solves, who it is for and what doesn’t work. Together, we choose the first useful version and what can wait.',
  },
  {
    term: 'First build',
    desc: 'We build the smallest useful version around your customers, your data and the way your team already works. You see it develop, try it with real scenarios and decide what needs a person’s approval.',
  },
  {
    term: 'Live, then next',
    desc: 'We watch and measure how people use it, where it falls short and what earns the next investment. We can keep improving it with you, or train your team and leave you with something you own and run.',
  },
]

const PEOPLE = [
  {
    term: 'Daryl Antony',
    tags: ['engineering', 'security', 'delivery'],
    desc: 'Daryl leads technical strategy and co-leads delivery. He turns the first version into a practical build: what to connect, what to keep simple and what needs to be reliable from day one.',
  },
  {
    term: 'Fabio Vella',
    tags: ['product', 'strategy', 'delivery'],
    desc: 'Fabio leads product strategy and co-leads delivery. He helps you find the useful problem, decide what belongs in the first release and keep the experience clear for the people using it.',
  },
]

function YeshumanTalkCta({ className }: { className?: string }) {
  const openContactSheet = useOpenYeshumanContactSheet()
  return (
    <Button
      type="button"
      variant="marketingSolid"
      size="marketing"
      pressFeedback
      className={cn(yeshumanUiSans, className)}
      onClick={() => openContactSheet()}
    >
      Talk to us
    </Button>
  )
}

function StepNumeral({ n }: { n: number }) {
  return (
    <div className="yeshuman-step-mark">
      <StepMark index={n - 1} />
      <span className={cn('yeshuman-numeral', yeshumanTabular)} aria-hidden>
        {n}
      </span>
    </div>
  )
}

function YeshumanHero() {
  return (
    <section aria-labelledby="yeshuman-hero-heading" className={cn('yeshuman-hero-band', yeshumanBand)}>
      <div className={cn(yeshumanContentColumn, 'yeshuman-hero-copy')}>
        <h1 id="yeshuman-hero-heading" className={yeshumanHeroHeading}>
          Ship the AI product your prototype{' '}
          <span className="yeshuman-underlined">
            promised.
            <HeadlineUnderline />
          </span>
        </h1>
        <p className={cn(yeshumanEditorial, 'mt-10 max-w-[34em]')}>
          Yes Human helps you turn an idea or early prototype into a scalable product people can
          rely on: something customer-facing, a useful feature inside your existing service, or an
          agent that takes on part of your team’s work.
        </p>
        <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-4">
          <YeshumanTalkCta />
          <p className={cn(yeshumanEditorialSm, 'max-w-[27em]')}>
            Start with a working session. Leave with a clear next step, whether or not we work
            together.
          </p>
        </div>
      </div>
      <HeroMark />
    </section>
  )
}

function YeshumanHelpSection({ anchorClass }: { anchorClass: string }) {
  return (
    <section
      id="who"
      tabIndex={-1}
      aria-labelledby="yeshuman-who-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass)}
    >
      <div className={cn(yeshumanContentColumn, 'yeshuman-split')}>
        <div>
          <h2 id="yeshuman-who-heading" className={yeshumanDisplayHeading}>
            Who we help
          </h2>
          <OverlapMark />
        </div>
        <div className="yeshuman-stack">
          {HELP.map((item, i) => (
            <div key={item.term}>
              {i > 0 ? <SectionRule /> : null}
              <h3 className="yeshuman-subhead">{item.term}</h3>
              <p className={cn('yeshuman-body', 'mt-3 max-w-[36em]')}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanHowSection({ anchorClass }: { anchorClass: string }) {
  return (
    <section
      id="how"
      tabIndex={-1}
      aria-labelledby="yeshuman-how-heading"
      className={cn(sectionPad, yeshumanBand, anchorClass)}
    >
      <div className={yeshumanContentColumn}>
        <h2 id="yeshuman-how-heading" className={yeshumanDisplayHeading}>
          How we work
        </h2>
        <ol className="mt-16 grid list-none gap-11 md:grid-cols-3 md:gap-14">
          {STEPS.map((step, i) => (
            <li key={step.term}>
              <StepNumeral n={i + 1} />
              <h3 className="yeshuman-subhead">{step.term}</h3>
              <p className="yeshuman-body mt-3">{step.desc}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function YeshumanPromiseStrip() {
  return (
    <section
      aria-label="What you leave with"
      className={cn(yeshumanBand, 'border-t border-[var(--marketing-border)]')}
    >
      <div className={cn(yeshumanContentColumn, 'py-[clamp(72px,8vw,112px)] text-center')}>
        <div className="relative mx-auto w-fit max-w-full">
          <PromiseMark />
          <p className={cn(yeshumanEditorialDisplay, 'max-w-[24em]')}>
            You leave the session with a shared picture of what to do next. It is yours whether or
            not we continue.
          </p>
        </div>
      </div>
    </section>
  )
}

function YeshumanPeopleSection({ anchorClass }: { anchorClass: string }) {
  return (
    <section
      id="about"
      tabIndex={-1}
      aria-labelledby="yeshuman-about-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass)}
    >
      <div className={yeshumanContentColumn}>
        <h2 id="yeshuman-about-heading" className={yeshumanDisplayHeading}>
          Who you will work with
        </h2>
        <p className={cn(yeshumanEditorial, 'mt-5 max-w-[32em]')}>
          The people you meet are the people who do the thinking, stay with you throughout the
          build and answer for what ships.
        </p>
        <div className="mt-16 grid gap-10 md:grid-cols-2 md:gap-14">
          {PEOPLE.map((person) => (
            <article key={person.term}>
              <h3 className="yeshuman-subhead">
                <span className="yeshuman-name">
                  {person.term}
                  <NameUnderline />
                </span>
              </h3>
              <p className="yeshuman-body mt-4 max-w-[32em]">{person.desc}</p>
              <ul className="yeshuman-tags mt-4" aria-label={`${person.term} works on`}>
                {person.tags.map((tag) => (
                  <li key={tag}>#{tag}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanCloseSection({ anchorClass }: { anchorClass: string }) {
  return (
    <section
      id="talk"
      tabIndex={-1}
      aria-labelledby="yeshuman-talk-heading"
      className={cn('yeshuman-close relative py-[clamp(88px,10vw,152px)] outline-none', anchorClass)}
    >
      <CloseMark />
      <div className={yeshumanContentColumn}>
        <h2 id="yeshuman-talk-heading" className={cn(yeshumanDisplayHeading, 'yeshuman-close-heading')}>
          Talk through what you have
        </h2>
        <p className={cn(yeshumanEditorial, 'mt-6 max-w-[32em]')}>
          Tell us about the problem, what you are working on, what is stuck and what you want to
          change. We will come ready to understand it, not to sell you a standard package.
        </p>
        <YeshumanTalkCta className="mt-10" />
      </div>
    </section>
  )
}

/** Router links change the hash without scrolling, so take the visitor to the section. */
function useScrollToHashSection() {
  const { hash, key } = useLocation()

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' })
  }, [hash, key])
}

export function YeshumanWorkbenchLanding() {
  useScrollToHashSection()
  const mainRef = useRef<HTMLElement>(null)
  useSketchMarks(mainRef)
  const { guestTheme } = useGuestSurfaceTheme()
  const client = CURRENT_CLIENT as ClientWithLanding
  const { chrome } = resolveLanding(client, guestTheme)
  const { scrollMt: sectionAnchor } = resolveMarketingStickyNavChrome(chrome.headerStyle)

  return (
    <YeshumanMarketingChrome>
      <PublicPageHead
        title={PAGE_TITLE}
        description={client.landing?.seoDescription}
        canonicalPath="/"
      />
      <main ref={mainRef} id="main" tabIndex={-1} className="outline-none">
        <PencilFilter />
        <YeshumanHero />
        <YeshumanHelpSection anchorClass={sectionAnchor} />
        <YeshumanHowSection anchorClass={sectionAnchor} />
        <YeshumanPromiseStrip />
        <YeshumanPeopleSection anchorClass={sectionAnchor} />
        <YeshumanCloseSection anchorClass={sectionAnchor} />
      </main>
    </YeshumanMarketingChrome>
  )
}
